import React, { useState } from 'react';
import { Popup } from 'semantic-ui-react';
import { useFavorites } from './FavoritesContext';
import { AddToFavoritesModal } from './AddToFavoritesModal';
import { CustomIcon } from '../icons';
import { useIsMobile } from '../../hooks';
import './FavoriteHeart.css';

interface FavoriteHeartProps {
  className?: string;
  style?: React.CSSProperties;
  placement?: 'page' | 'sidebar';
}

export const FavoriteHeart: React.FC<FavoriteHeartProps> = ({
  className = '',
  style = {},
  placement = 'page'
}) => {
  const {
    canFavorite,
    isFavorited,
    addFavorite,
    canAddMore,
    maxFavorites,
    favorites
  } = useFavorites();
  
  const isMobile = useIsMobile();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);

  const handleHeartClick = () => {
    if (isFavorited) {
      // Already favorited - show notification
      return;
    }

    if (!canFavorite) {
      // Can't favorite this page
      return;
    }

    if (!canAddMore) {
      // Max favorites reached
      return;
    }

    // Open modal to get name
    setIsModalOpen(true);
  };

  const handleAddFavorite = (name: string) => {
    // The context now handles URL tracking automatically
    const success = addFavorite(name);
    
    if (success) {
      setShowAnimation(true);
      setTimeout(() => setShowAnimation(false), 600);
    }
    setIsModalOpen(false);
  };

  const getPopupContent = () => {
    if (isFavorited) {
      return 'Already in favorites';
    }
    if (!canFavorite) {
      return 'This page cannot be added to favorites';
    }
    if (!canAddMore) {
      return `Maximum ${maxFavorites} favorites reached`;
    }
    return 'Add to favorites';
  };
  const getHeartIcon = () => {
    // Always use heart-01 CustomIcon for both states
    return 'heart-01';
  };

  const getHeartClass = () => {
    let classes = ['favorite-heart'];
    
    if (placement === 'sidebar') {
      classes.push('favorite-heart-sidebar');
    } else {
      classes.push('favorite-heart-page');
    }

    if (isFavorited) {
      classes.push('favorite-heart-active');
    } else if (canFavorite && canAddMore) {
      classes.push('favorite-heart-available');
    } else {
      classes.push('favorite-heart-disabled');
    }

    if (showAnimation) {
      classes.push('favorite-heart-adding');
    }

    return classes.join(' ');
  };

  if (isMobile || (!canFavorite && !isFavorited)) {
    return null; // Don't show heart on mobile or blacklisted pages
  }

  return (
    <>
      <Popup
        content={getPopupContent()}
        trigger={          
        <div
            className={`${getHeartClass()} ${className}`}
            style={style}
            onClick={handleHeartClick}
          >
            <CustomIcon 
              name={getHeartIcon()} 
              className="favorite-heart-icon"
            />
          </div>
        }
        position={placement === 'sidebar' ? 'right center' : 'top center'}
        inverted
        size="mini"
        disabled={isFavorited && canFavorite}
      />

      <AddToFavoritesModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleAddFavorite}
      />
    </>
  );
};
